import { Helmet } from "react-helmet";
import { useHistory } from "react-router";
import { FormProvider, useForm } from "react-hook-form";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import Layout from "../../../../components/Layout";
import { Heading, Text } from "../../../../components/Type";
import Button from "../../../../components/Button";
import { ArrowRight } from "../../../../components/Icons/general-interface-1";
import EmptyCard from "../../../../components/EmptyCard";
import SectionHeading from "../../../../components/SectionHeading";
import ProfilePreview from "../../../../components/UserInterface/ProfilePreview";
import TextInput from "../../../../components/TextInput";
import NotificationBanner from "../../../../components/NotificationBanner";
import styles from "./index.module.scss";
import { phoneExtensionFieldOptions, phoneNumberFieldOptions } from "../../../../utils/forms/validation";
import { formatPhoneNumber } from "../../../../utils/forms/formatting";

const GET_USER = gql`
  query User {
    me: self {
      first_name
      last_name
      adp_auth_num
      phone
      phone_ext
      locations {
        id
        name
        adp_auth_num
        address {
          address_1
          address_2
          city
          postal_code
          province
        }
      }
      organization {
        name
        address {
          name
          address_1
          address_2
          unit_number
          street_number
          street_name
          lot
          city
          postal_code
          province
        }
      }
      confirmed
      blocked
      email
      id
      username
      role {
        id
        description
        type
        name
      }
    }
  }
`;

const UPDATE_PROFILE = gql`
  mutation UpdateUser($input: updateUserInput) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;

const UserEditPage = () => {
  const {
    state: { user },
  } = useStoreContext();
  const history = useHistory();
  const { data, loading, error } = useQuery(GET_USER, {
    fetchPolicy: "no-cache",
  });
  const [updateProfile, { error: updateError }] = useMutation(UPDATE_PROFILE, {
    onCompleted: () => history.push("/admin/profile"),
  });
  const methods = useForm({ mode: "onChange" });

  const handleSubmit = async (values) => {
    await updateProfile({
      variables: {
        input: {
          data: {
            phone: formatPhoneNumber(values.phone),
            adp_auth_num: values.adp_auth_num,
            phone_ext: values.phone_ext,
          },
          where: {
            id: user?.id,
          },
        },
      },
    });
  };

  return (
    <Layout>
      <Helmet>
        <title>Edit Profile</title>
      </Helmet>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <Heading size="h3" className={styles.profileName}>
              Edit Profile
            </Heading>
          </div>
          <div className={styles.headerAction}>
            <Button
              primary
              submit
              icon={<ArrowRight />}
              onClick={methods.handleSubmit(handleSubmit)}
            >
              Save Profile
            </Button>
          </div>
        </div>
        {error && error.message}
        {loading ? (
          "loading..."
        ) : (
          <div className={styles.grid}>
            <div className={styles.sidebar}>
              <EmptyCard>
                <ProfilePreview size="medium" user={data.me} userRole />
                <div className={styles.email}>
                  <Text>Email</Text>
                  <Text className={styles.emailText}>{data.me.email}</Text>
                </div>
              </EmptyCard>
            </div>
            <div className={styles.content}>
              {updateError && (
                <NotificationBanner
                  error
                  content="Something went wrong, please try again."
                  className={styles.error}
                />
              )}
              <FormProvider {...methods}>
                <form>
                  <EmptyCard noPad>
                    <SectionHeading title="Contact Information" />
                    <div className={styles.sectionGrid}>
                      <div className={styles.phoneCol}>
                        <TextInput
                          id="phone"
                          label="* Phone Number"
                          defaultValue={data.me.phone}
                          placeholder="000-000-0000"
                          fieldOptions={{
                            ...phoneNumberFieldOptions,
                            required: "Enter your phone number",
                          }}
                        />
                      </div>
                      <div className={styles.extCol}>
                        <TextInput
                          id="phone_ext"
                          label="Ext"
                          placeholder="000"
                          fieldOptions={phoneExtensionFieldOptions}
                          defaultValue={data.me.phone_ext}
                        />
                      </div>
                    </div>
                  </EmptyCard>
                </form>
              </FormProvider>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserEditPage;
